import { useCallback, useEffect, useState } from "react";
import { getEnrollCourses } from "../../../../services/StudentPortal/StudentCourseApi";
import { useStudentSession } from "../../context/StudentSessionContext";
import { handleApiError } from "../../../../utils/errorHandler";

// The only confirmed shape so far is the error case — {success:false,
// message:"User Not Found"} — for an unrecognised admission number. The
// success shape isn't confirmed yet, so this tries every reasonable
// candidate for where the course list lives instead of assuming one.
const extractCourseList = (data) => {
  if (!data) return [];
  if (data.success === false) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.courses)) return data.courses;
  if (Array.isArray(data.result)) return data.result;
  return [];
};

/**
 * "Courses" lists the logged-in student's enrolled Moodle courses.
 * Exposes { courses, isLoading } for StudentCourses.js.
 */
export const useStudentCourses = () => {
  const { student } = useStudentSession();
  const idNumber = student?.id_number;
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCourses = useCallback(async () => {
    if (!idNumber) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await getEnrollCourses(idNumber);
      setCourses(extractCourseList(response?.data));
    } catch (error) {
      // A "User Not Found" 404 just means Moodle has no record for this
      // admission number yet — not worth a toast every time.
      if (error?.response?.status !== 404) {
        handleApiError(error);
      }
      setCourses([]);
    } finally {
      setIsLoading(false);
    }
  }, [idNumber]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  return { courses, isLoading, refetch: fetchCourses };
};
